/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import './ChatWindow.css';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import ChatContext from '../../contexts/data/ChatContext';
import ScenariosContext from '../../contexts/data/ScenariosContext';
import { Flex, Spin, Switch, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { ESmsRole } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { CheckOutlined, LeftOutlined, ClearOutlined, DeleteOutlined, PlusOutlined, StarFilled, StarOutlined, CopyOutlined, WhatsAppOutlined, MessageOutlined, SendOutlined, FilterOutlined, CheckCircleFilled, ExclamationCircleFilled, EditOutlined, RobotOutlined } from '@ant-design/icons';
import { getTagColor } from '../../../core/utils/commonUtils';
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { 
  JobType,
  jobTypeAsString,
  NotificationType,
  notificationTypeAsString,
  UserRole,
  ContextStatus,
  EPhonePlatform,
  EChannel,
  channelAsString,
} from '@leedpulse-monorepo/enums';
import Modal from '../job/ModalCreateJob';
import { Modal as AntdModal, Button } from 'antd';
import SMS from '../../../domain/models/SMS';
import ChatTextArea from './ChatTextArea';
import Deliverability, { deliverabilityAsString } from '../../../domain/enums/@shared/Deliverability';
import ContactDetailsModal from './contact/ContactDetailsModal';
import Job from '../../../domain/models/Job';
import { useNavigate } from 'react-router-dom';

interface ChatWindowProps {
  contactId: string;
  multiMode?: boolean;
  onReadAll?: () => void;
  onOneMessageRead?: (becomeRead: boolean, role: ESmsRole) => void;
  onMobileNavBack?: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  contactId,
  multiMode = false,
  onReadAll,
  onOneMessageRead,
  onMobileNavBack,
}) => {
  const navigate = useNavigate();
  const [hasScrolled, setHasScrolled] = useState(false);
  const chatContext = useContext(ChatContext);
  const messagesRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const authContext = useContext(AuthenticationContext);
  const scenariosContext = useContext(ScenariosContext);

  // Lu directement depuis le contexte : la copie en state local n'apportait
  // rien et imposait un rendu de plus a chaque arrivee des scenarios.
  const scenarios = scenariosContext.allScenarios;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [, setSelectedJobType] = useState<JobType | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isContactDetailsModalVisible, setIsContactDetailsModalVisible] = useState(false);

  useEffect(() => {
    if (
      chatContext.status === ContextStatus.IDLE ||
      chatContext.currentContact?.id !== contactId
    ) {
      chatContext.fetchAllMessages(contactId);
      if (scenariosContext.allScenarios.length === 0) {
        scenariosContext.fetchAll();
      }
    }
    return () => {
      chatContext.reset();
    };
  }, []);

  // Reset du flag de scroll pendant le rendu (pattern « ajuster l'etat quand une
  // prop change » de la doc React) : dans l'effet il coutait un second rendu a
  // chaque changement de contact. Le fetch, lui, reste un effet de bord.
  const [lastContactId, setLastContactId] = useState(contactId);
  if (contactId !== lastContactId) {
    setLastContactId(contactId);
    setHasScrolled(false);
  }

  useEffect(() => {
    chatContext.fetchAllMessages(contactId);
  }, [contactId]);

  useEffect(() => {
    if (messagesRef.current && chatContext.status === ContextStatus.SUCCESS) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      setHasScrolled(true);
    }
  }, [chatContext.status]);

  useEffect(() => {
    if (messagesRef.current && chatContext.items.length > 0 && !hasScrolled) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      setHasScrolled(true);
    }
  }, [chatContext.items]);

  const isReadOnly =
    authContext.authentication === null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  // Handle Modal for Job
  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleCreateJob = (jobType: JobType, scheduledTime?: number) => {
    setSelectedJobType(jobType);
    setIsModalVisible(false);

    if (chatContext.currentContact && authContext.authentication) {
      const newJob = {
        type: jobType,
        timestamp: scheduledTime,
      };
      chatContext.handleCreateJob(newJob as unknown as Job);
    }
  };

  // Handle Modal for Delete Job
  const handleOpenDeleteModal = () => setIsDeleteModalVisible(true);
  const handleCloseDeleteModal = () => setIsDeleteModalVisible(false);

  // Handle Modal for Contact details
  const handleOpenContactDetailsModal = () => setIsContactDetailsModalVisible(true);
  const handleCloseContactDetailsModal = () => setIsContactDetailsModalVisible(false);

  const handleDeleteJob = () => {
    if (chatContext.nextJob) {
      chatContext.handleDeleteJob(chatContext.nextJob);
    }
    setIsDeleteModalVisible(false);
  };

  const renderMessageStatus = (message: SMS) => {

    if ([Deliverability.delivered, Deliverability.received].some(status => message.deliverability.includes(status))) {
      return <CheckCircleFilled style={{ color: '#00E396' }} />
    } else if ([Deliverability.queued].some(status => message.deliverability.includes(status))) {
      return <FilterOutlined style={{ color: '#FFD166' }} />
    } else if ([Deliverability.na, Deliverability.new, Deliverability.sent, Deliverability.validated].some(status => message.deliverability.includes(status))) {
      return <SendOutlined style={{ color: '#FFD166' }} />
    } else if ([Deliverability.error, Deliverability.delivery_failed, Deliverability.invalid, Deliverability.expired].some(status => message.deliverability.includes(status))) {
      return <ExclamationCircleFilled style={{ color: '#FF4560' }} />
    }
    return null;
  };

  const renderChatHeader = (): React.ReactNode => {
    return (
      <div
        className={classNames({
          'chat-window__contact-header': true,
          'chat-window__contact-header--loading':
            chatContext.status === ContextStatus.LOADING,
        })}
      >
        {onMobileNavBack && (
          <div
            className="chat-window__contact-header__mobile-back-nav"
            onClick={() => onMobileNavBack()}
          >
            <LeftOutlined />
          </div>
        )}

        {renderChatHeaderContent()}
        {renderChatHeaderContainer()}

        <Button
          style={{
            marginInline: '10px',
          }}
          icon={<ClearOutlined />}
          disabled={isReadOnly}
          loading={
            chatContext.status === ContextStatus.LOADING ||
            chatContext.sendMessageStatus === ContextStatus.LOADING
          }
          onClick={() => {
            if (
              !chatContext.items.some((message) => !message.readedBackoffice)
            ) {
              return;
            }
            chatContext.setAllMessagesAsRead();
            if (onReadAll) {
              onReadAll();
            }
          }}
        >
          {windowSize.width > 576 ? 'Tout marquer comme lu' : ''}
        </Button>
        <div className="chat-window__contact-header__ai-controls">
          <Switch
            checkedChildren="▶ IA"
            unCheckedChildren="⏸ IA"
            value={
              !chatContext.currentContact?.messages_settings.ia_pause
            }
            onChange={(_) => {
              chatContext.toggleAIPauseForCurrentContact();
            }}
          />
        </div>
      </div>
    );
  };

  const renderChatHeaderContent = (): React.ReactNode => {
    return (
      <div
        className="chat-window__contact-header__content"
        onClick={() => handleOpenContactDetailsModal()}
      >
        <span>
          {chatContext.currentContact?.contact.firstname}{' '}
          {chatContext.currentContact?.contact.lastname}{' '}
          (
          <a
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/subscriptions/${chatContext?.currentSubscription?.id}`);
            }}
          >
            {chatContext.currentSubscription?.name}
          </a>
          )
        </span>
        <span>{chatContext.currentContact?.contact.phone}</span>
        <span>{chatContext.currentContact?.contact.email}</span>
        <Tag
          className='chat-window__contact-header__content__tag'
          color={getTagColor(chatContext.currentContact?.qualification_ai?.qualification || "not_available")}
        >
          &#8226; {getInterestLevel(chatContext.currentContact?.qualification_ai?.qualification || "not_available")}
        </Tag>
        <Tag className='contact-tile__content__owner-tag' color='lightgray'>
          {scenarios.find(scenario => scenario.id === chatContext.currentContact?.contact?.prospect_category)?.name || chatContext.currentContact?.contact?.prospect_category}
        </Tag>
      </div>
    );
  }

  const renderChatHeaderContainer = (): React.ReactNode => {
    return (
      <div className="chat-window__contact-header__container">
        <div className="chat-window__contact-header__copy">
          <Tooltip title="Copier le lien de la conversation">
            <Button
              type={'default'}
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                chatContext.copyChatLink();
              }}
            />
          </Tooltip>
        </div>
        <div className="chat-window__contact-header__star">
          <Tooltip
            title={
              chatContext.currentContact?.is_favorite_backoffice
                ? "Retirer des favoris"
                : "Ajouter aux favoris"
            }
          >
            <Button
              type={'default'}
              icon={
                chatContext.currentContact?.is_favorite_backoffice ? (
                  <StarFilled />
                ) : (
                  <StarOutlined />
                )
              }
              className={`star-button ${chatContext.currentContact?.is_favorite_backoffice ? "selected" : ""
                }`}
              onClick={(e) => {
                e.stopPropagation();
                chatContext.toggleFavoriteBackoffice();
              }}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderChatWindowMessages = (): React.ReactNode => {
    return (
      <div className="chat-window__messages" ref={messagesRef}>
        {chatContext.status === ContextStatus.LOADING ? (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin />
          </div>
        ) : (
          <>
            {[...chatContext.items]
              .filter((message) => message.role !== ESmsRole.system)
              .reverse()
              .map((message, index) => {
                return (
                  renderChatBubble(message, index)
                );
              })}
            {chatContext.notifications && (
              <div className="chat-window__messages__notificationsContainer">
                {chatContext.notifications.map((notification, index) => (
                  <div key={index} className="chat-window__messages__notificationsContainer">
                    <div className="chat-window__notification">
                      {notification.type === NotificationType.TODO ? (
                        <>
                          <span>{jobTypeAsString(JobType.followUpToDo)}</span>
                          <span className='chat-window__notification_subtitle'>Notification envoyée le {moment(notification.created).fromNow()}</span>
                        </>
                      ) : (
                        <span>Qualifié en {notificationTypeAsString(notification.type, notification.days)}, le {moment(notification.updated).format('LL')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {chatContext.nextJob ? (
              <div className="chat-window__messages__jobContainer">
                <div className='chat-window__messages__job'>
                  <span>{jobTypeAsString(chatContext.nextJob.type)}</span>
                  <span>{new Date(chatContext.nextJob.timestamp).toLocaleString()}</span>
                </div>
                <button
                  className="chat-window__messages__job__button"
                  onClick={handleOpenDeleteModal}
                >
                  <DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />
                </button>
                <button
                  className="chat-window__messages__job__button"
                  onClick={handleOpenModal}
                >
                  <PlusOutlined style={{ fontSize: '16px', color: '#999' }} />
                </button>
              </div>
            ) : (
              <div className="chat-window__messages__jobContainerEmpty">
                <div className='chat-window__messages__jobEmpty' onClick={handleOpenModal}>
                  <span>Ajouter une action à l'IA pour ce prospect</span>
                </div>
                <button
                  className="chat-window__messages__job__button"
                  onClick={handleOpenModal}
                >
                  <PlusOutlined style={{ fontSize: '16px', color: '#999' }} />
                </button>
              </div>
            )}
            <AntdModal
              title="Confirmer la suppression"
              open={isDeleteModalVisible}
              onOk={handleDeleteJob}
              onCancel={handleCloseDeleteModal}
              okText="Oui"
              cancelText="Non"
            >
              <p>Es-tu sûr de vouloir supprimer cette action programmée ?</p>
            </AntdModal>
          </>
        )}

        <Modal
          visible={isModalVisible}
          title="Programmer une nouvelle action"
          onClose={handleCloseModal}
          onConfirm={handleCreateJob}
        />

        <ContactDetailsModal contactId={contactId} visible={isContactDetailsModalVisible} onCancel={handleCloseContactDetailsModal} />
      </div>
    );
  };

  const renderChatBubble = (message: SMS, index: number): React.ReactNode => {
    return (
      <div
        key={message.id}
        className={classNames('chat-window__messages__message', {
          'chat-window__messages__message--sent':
            message.role === ESmsRole.assistant,
          'chat-window__messages__message--received':
            message.role === ESmsRole.user,
          'chat-window__messages__message--last-of-group':
            [...chatContext.items]
              .filter((message) => message.role !== ESmsRole.system)
              .reverse()[index + 1]?.role !== message.role,
        })}
      >
        <div
          className={classNames({
            'chat-window__messages__message_read-status-dot':
              true,
            'chat-window__messages__message_read-status-dot--readed':
              message.readedBackoffice,
          })}
          onClick={() =>
            chatContext.toggleOneSMSReadStatus(
              message,
              onOneMessageRead
                ? (becomeRead) =>
                  onOneMessageRead(becomeRead, message.role)
                : undefined
            )
          }
        >
          {message.readedBackoffice && (
            <CheckOutlined
              style={{
                color: 'white',
                fontSize: '10px',
              }}
            />
          )}
        </div>
        <p style={{ fontWeight: 400, whiteSpace: "pre-line" }}>{message.message}</p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            gap: "10px",
            alignItems: 'center',
          }}
        >
          {
            message.role === ESmsRole.assistant ?
              <Tooltip title={deliverabilityAsString(message.deliverability)}>
                {renderMessageStatus(message)}
              </Tooltip> :
              null
          }
          {
            <Tooltip title={getChannel(message.platform)}>
              {getChannelIcon(message.platform)}
            </Tooltip>
          }
          <span>{message.created.toLocaleString()}</span>
          {(message.role === ESmsRole.system || message.role === ESmsRole.assistant) && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: message.manualsending ? 'bold' : '',
                color: message.manualsending ? 'white' : '',
                opacity: message.manualsending ? 1 : 0.7,
              }}
            >
              {message.manualsending ? 
              <Flex align="center"><EditOutlined style={{ marginRight: 6 }} /> Humain</Flex> : 
              <Flex align="center"><RobotOutlined style={{ marginRight: 6 }} /> IA</Flex>}
            </span>
          )}
        </div>
      </div>
    );
  };

  const getChannel = (platform: EPhonePlatform): string => {
    switch (platform) {
      case EPhonePlatform.sakari:
      case EPhonePlatform.callr:
        return channelAsString(EChannel.sms);
      case EPhonePlatform.twilio:
        return channelAsString(EChannel.whatsapp);
      default:
        return '';
    }
  };

  const getChannelIcon = (platform: EPhonePlatform) => {
    switch (platform) {
      case EPhonePlatform.sakari:
      case EPhonePlatform.callr:
        return <MessageOutlined />;
      case EPhonePlatform.twilio:
        return <WhatsAppOutlined />;
      default:
        return <MessageOutlined />;
    }
  };

  const renderChatMessageBox = (): React.ReactNode => {
    return (
      <ChatTextArea
        placeholder='Ecrivez un SMS ici'
        loading={
          chatContext.status === ContextStatus.LOADING ||
          chatContext.sendMessageStatus === ContextStatus.LOADING
        }
        onMessageSent={(message) => chatContext.sendMessageToContact(message)} />
    );
  };

  return (
    <div
      className={classNames({
        'chat-window': true,
        'chat-window--multi': multiMode,
      })}
    >
      {renderChatHeader()}
      {renderChatWindowMessages()}
      {renderChatMessageBox()}
    </div>
  );
};

export default ChatWindow;
